import { useState, useEffect, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { useDebounce, DEBOUNCE_DELAYS } from "@/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { inboxService } from "@/services/inboxService";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import {
  ConversationStatus,
  defaultVisibleStatuses,
} from "@/types/conversation";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MainLayout from "@/components/layout/MainLayout";
import { supabase } from "@/integrations/supabase/client";
import {
  InboxFilters,
  ConversationListPanel,
  ConversationDetailsPanel,
  ConversationInfoPanel,
} from "@/components/inbox-new";
import {
  Conversation,
  Message,
  FormField,
  DatabaseMessage,
  ConversationDetailsResponse,
} from "@/types/inbox.types";

// Replace imports with InboxContext
import {
  useInboxConversations,
  useInboxConversationDetails,
} from "@/contexts/InboxContext";

const InboxNew = () => {
  const { agents } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();

  // Use context hooks instead of local state
  const {
    conversations,
    isLoading,
    refresh: refreshConversations,
  } = useInboxConversations();
  const {
    conversation: conversationInfo,
    messages: conversationMessages,
    formFields: conversationFormFields,
    isLoading: loadingConversation,
    select: selectConversation,
    updateStatus: updateConversationStatus,
  } = useInboxConversationDetails();

  // Keep local UI state
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAYS.SEARCH);
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);
  const [selectedAgentId, setSelectedAgentId] = useState<string>("all");
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userTypeFilter, setUserTypeFilter] = useState<
    "all" | "leads" | "clients"
  >("all");
  const [sentimentFilter, setSentimentFilter] = useState<string>("all");
  const [showHighPriority, setShowHighPriority] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ConversationStatus[]>(
    defaultVisibleStatuses,
  );
  const [activeTab, setActiveTab] = useState("inbox");

  // Handle tab change to navigate between inbox and contacts
  const handleTabChange = (value: string) => {
    if (value === "list") {
      navigate("/contacts");
    } else {
      setActiveTab(value);
    }
  };

  // Parse URL query parameters for agent filtering
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const agentParam = queryParams.get("agent");
    if (agentParam) {
      setSelectedAgentId(agentParam);
    }
  }, [location.search]);

  // Update URL when agent filter changes
  useEffect(() => {
    if (selectedAgentId === "all") {
      navigate("/inbox", { replace: true });
    } else {
      navigate(`/inbox?agent=${selectedAgentId}`, { replace: true });
    }
  }, [selectedAgentId, navigate]);

  // Set loading state based on organization provider loading
  useEffect(() => {
    setLoadingAgents(!agents);
  }, [agents]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshConversations();
    } catch (error) {
      console.error("Failed to refresh conversations:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle conversation selection
  const handleSessionClick = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    selectConversation(conversationId);
  };

  // Filter conversations based on search term, agent filter, and new filters
  const filteredConversations = useMemo(() => {
    return (
      conversations
        // First filter out anonymous conversations (those without an agent_chat_user_id)
        .filter(
          (conv) =>
            conv.agent_chat_user_id && conv.agent_chat_user_id.trim() !== "",
        )
        // Then filter by agent if selected
        .filter(
          (conv) =>
            selectedAgentId === "all" || conv.agent_id === selectedAgentId,
        )
        // Filter by user type
        .filter((conv) => {
          if (userTypeFilter === "leads") {
            return conv.user_type === "lead";
          } else if (userTypeFilter === "clients") {
            return conv.user_type === "client";
          }
          return true; // "all"
        })
        // Filter by sentiment
        .filter((conv) => {
          if (sentimentFilter !== "all") {
            return conv.sentiment === sentimentFilter;
          }
          return true;
        })
        // Filter by high priority
        .filter((conv) => {
          if (showHighPriority) {
            return conv.priority === "urgent" || conv.priority === "high";
          }
          return true;
        })
        // Filter by status
        .filter((conv) => {
          const convStatus = conv.status || "open"; // Default to 'open' if no status
          return statusFilter.includes(convStatus as ConversationStatus);
        })
        // Apply comprehensive search filter (using debounced search term)
        // Searches across: summary, user name, user email, intent, next step, tags, form fields, and form field values
        .filter((conv) => {
          if (!debouncedSearchTerm) return true;

          const searchLower = debouncedSearchTerm.toLowerCase();

          // Search in summary
          const summaryMatch = (conv.summary ?? "")
            .toLowerCase()
            .includes(searchLower);

          // Search in user name
          const userNameMatch = (conv.user_name ?? "")
            .toLowerCase()
            .includes(searchLower);

          // Search in user email
          const userEmailMatch = (conv.user_email ?? "")
            .toLowerCase()
            .includes(searchLower);

          // Search in intent
          const intentMatch = (conv.intent ?? "")
            .toLowerCase()
            .includes(searchLower);

          // Search in next step
          const nextStepMatch = (conv.next_step ?? "")
            .toLowerCase()
            .includes(searchLower);

          // Search in tags array
          const tagsMatch =
            conv.tags &&
            conv.tags.some((tag) => tag.toLowerCase().includes(searchLower));

          // Search in form fields if they exist
          const fieldMatch =
            conv.form_fields &&
            Object.values(conv.form_fields).some(
              (value) =>
                typeof value === "string" &&
                value.toLowerCase().includes(searchLower),
            );

          // Search in form field values if they exist
          const fieldValuesMatch =
            conv.form_field_values &&
            Object.values(conv.form_field_values).some(
              (value) =>
                typeof value === "string" &&
                value.toLowerCase().includes(searchLower),
            );

          return (
            summaryMatch ||
            userNameMatch ||
            userEmailMatch ||
            intentMatch ||
            nextStepMatch ||
            tagsMatch ||
            fieldMatch ||
            fieldValuesMatch ||
            false
          );
        })
    );
  }, [
    conversations,
    selectedAgentId,
    userTypeFilter,
    sentimentFilter,
    showHighPriority,
    debouncedSearchTerm,
    statusFilter,
  ]);

  // Listen for real-time updates to conversations
  useEffect(() => {
    const channel = supabase
      .channel("public:conversations")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "conversations",
        },
        (payload) => {
          // Only react to changes if conversations have been loaded
          if (conversations.length === 0) return;

          if (payload.eventType === "UPDATE") {
            const updatedConversation = payload.new as Partial<Conversation>;

            // Update conversation in the list
            const updatedConversations = conversations.map((conv) =>
              conv.id === updatedConversation.id
                ? {
                    ...conv,
                    state: updatedConversation.state as "open" | "completed",
                  }
                : conv,
            );

            // setConversations(updatedConversations); // This line was removed from the new_code

            // Update the cache with the new data
            // saveConversationsToCache(updatedConversations); // This line was removed from the new_code

            // Update selected conversation if it's the one that changed
            if (selectedConversationId === updatedConversation.id) {
              selectConversation(updatedConversation.id);
            }
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversations.length, selectedConversationId]);

  return (
    <TooltipProvider>
      <MainLayout>
        {/* Fixed PageHeader */}
        <div className="border-b bg-white">
          <div className="px-6 py-4">
            <div className="flex items-center gap-4">
              <h1 className="text-lg font-semibold">Chats</h1>
              <Tabs
                value={activeTab}
                onValueChange={handleTabChange}
                className="w-64"
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="inbox">Inbox</TabsTrigger>
                  <TabsTrigger value="list">List View</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Fixed Filter Row */}
          <InboxFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            selectedAgentId={selectedAgentId}
            onAgentChange={setSelectedAgentId}
            agents={agents}
            userTypeFilter={userTypeFilter}
            onUserTypeChange={setUserTypeFilter}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            showHighPriority={showHighPriority}
            onHighPriorityChange={setShowHighPriority}
            sentimentFilter={sentimentFilter}
            onSentimentChange={setSentimentFilter}
            conversationCount={filteredConversations.length}
            isRefreshing={isRefreshing}
            onRefresh={handleRefresh}
          />

          {/* Main content area with proper flex structure */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left column - Conversation list */}
            <ConversationListPanel
              isLoading={isLoading}
              conversations={filteredConversations}
              selectedConversationId={selectedConversationId}
              searchTerm={debouncedSearchTerm}
              onConversationClick={handleSessionClick}
            />

            {/* Middle column - Chat messages */}
            <ConversationDetailsPanel
              selectedConversationId={selectedConversationId}
              loadingConversation={loadingConversation}
              conversationInfo={conversationInfo}
              conversationMessages={conversationMessages}
              onBackClick={() => setSelectedConversationId(null)}
              onStatusChange={(newStatus) => {
                if (conversationInfo?.id) {
                  updateConversationStatus(conversationInfo.id, newStatus);
                }
              }}
            />

            {/* Right column - Details */}
            <ConversationInfoPanel
              selectedConversationId={selectedConversationId}
              loadingConversation={loadingConversation}
              conversationInfo={conversationInfo}
              conversationFormFields={conversationFormFields}
              conversationMessages={conversationMessages}
            />
          </div>
        </div>
      </MainLayout>
    </TooltipProvider>
  );
};

export default InboxNew;
